/**
 * Terms Acceptance Page
 * SCR-802: Terms & Conditions Acceptance
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLegalDocument, useAcceptTerms } from '../hooks/useAuth';

interface DocumentVersion {
  type: 'terms' | 'privacy';
  version: string;
  updatedAt: string;
}

export function TermsAcceptancePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/dashboard';

  const [activeTab, setActiveTab] = useState<'terms' | 'privacy'>('terms');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [termsScrolled, setTermsScrolled] = useState(false);
  const [privacyScrolled, setPrivacyScrolled] = useState(false);

  const { data: termsDoc, isLoading: termsLoading } = useLegalDocument('terms');
  const { data: privacyDoc, isLoading: privacyLoading } = useLegalDocument('privacy');
  const acceptMutation = useAcceptTerms();

  const documentVersions: DocumentVersion[] = [
    { type: 'terms', version: termsDoc?.version || '2.0', updatedAt: termsDoc?.effectiveDate || '2024-01-15' },
    { type: 'privacy', version: privacyDoc?.version || '1.5', updatedAt: privacyDoc?.effectiveDate || '2024-01-15' },
  ];

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    if (atBottom) {
      if (activeTab === 'terms') {
        setTermsScrolled(true);
      } else {
        setPrivacyScrolled(true);
      }
    }
  };

  const handleAccept = async () => {
    try {
      await acceptMutation.mutateAsync({
        termsVersion: documentVersions.find((d) => d.type === 'terms')?.version || '',
        privacyVersion: documentVersions.find((d) => d.type === 'privacy')?.version || '',
        acceptedAt: new Date().toISOString(),
      });
      navigate(redirectTo);
    } catch (error) {
      console.error('Failed to accept terms:', error);
    }
  };

  const canAccept = termsAccepted && privacyAccepted;

  return (
    <div className="min-h-screen bg-mv-gray-100 flex items-center justify-center px-4 py-8">
      <div className="max-w-3xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-mv-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-mv-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-mv-gray-900 mb-2">Algemene voorwaarden</h1>
          <p className="text-mv-gray-600">
            Lees en accepteer onze voorwaarden om door te gaan
          </p>
        </div>

        {/* Document Tabs */}
        <div className="bg-white rounded-lg border border-mv-gray-200 overflow-hidden">
          <div className="flex border-b border-mv-gray-200">
            <button
              onClick={() => setActiveTab('terms')}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'terms'
                  ? 'text-mv-blue-600 border-b-2 border-mv-blue-600 bg-mv-blue-50'
                  : 'text-mv-gray-600 hover:text-mv-gray-900'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <span>Algemene Voorwaarden</span>
                {termsScrolled && (
                  <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('privacy')}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'privacy'
                  ? 'text-mv-blue-600 border-b-2 border-mv-blue-600 bg-mv-blue-50'
                  : 'text-mv-gray-600 hover:text-mv-gray-900'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <span>Privacybeleid</span>
                {privacyScrolled && (
                  <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </button>
          </div>

          {/* Document Content */}
          <div
            className="h-96 overflow-y-auto p-6"
            onScroll={handleScroll}
          >
            {activeTab === 'terms' ? (
              termsLoading ? (
                <div className="animate-pulse space-y-4">
                  <div className="h-4 bg-mv-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-mv-gray-200 rounded w-full" />
                  <div className="h-4 bg-mv-gray-200 rounded w-5/6" />
                </div>
              ) : (
                <div className="prose prose-sm max-w-none">
                  <h2 className="text-lg font-semibold mb-4">Algemene Voorwaarden myVault</h2>
                  <p className="text-mv-gray-600 text-sm mb-2">
                    Versie {documentVersions[0].version} - Laatst bijgewerkt: {documentVersions[0].updatedAt}
                  </p>
                  <div className="space-y-4 text-mv-gray-700">
                    <section>
                      <h3 className="font-medium">1. Definities</h3>
                      <p>In deze voorwaarden wordt verstaan onder:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li><strong>myVault:</strong> De digitale kluisdienst voor het veilig opslaan van documenten.</li>
                        <li><strong>Gebruiker:</strong> De natuurlijke persoon die een account aanmaakt.</li>
                        <li><strong>Documenten:</strong> Alle bestanden die door de gebruiker worden geüpload.</li>
                      </ul>
                    </section>
                    <section>
                      <h3 className="font-medium">2. Gebruik van de dienst</h3>
                      <p>Door gebruik te maken van myVault gaat u akkoord met deze voorwaarden.</p>
                    </section>
                    <section>
                      <h3 className="font-medium">3. Privacy en gegevensbescherming</h3>
                      <p>Wij verwerken uw gegevens conform de AVG (GDPR). Zie ons privacybeleid voor details.</p>
                    </section>
                    <section>
                      <h3 className="font-medium">4. Beveiliging</h3>
                      <p>Alle documenten worden versleuteld opgeslagen. U bent verantwoordelijk voor het geheimhouden van uw inloggegevens.</p>
                    </section>
                    <section>
                      <h3 className="font-medium">5. Aansprakelijkheid</h3>
                      <p>myVault is niet aansprakelijk voor schade door ongeoorloofd gebruik van uw account.</p>
                    </section>
                    <section>
                      <h3 className="font-medium">6. Beëindiging</h3>
                      <p>U kunt uw account op elk moment verwijderen. Wij behouden ons het recht voor accounts te beëindigen bij misbruik.</p>
                    </section>
                  </div>
                </div>
              )
            ) : privacyLoading ? (
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-mv-gray-200 rounded w-3/4" />
                <div className="h-4 bg-mv-gray-200 rounded w-full" />
                <div className="h-4 bg-mv-gray-200 rounded w-5/6" />
              </div>
            ) : (
              <div className="prose prose-sm max-w-none">
                <h2 className="text-lg font-semibold mb-4">Privacybeleid myVault</h2>
                <p className="text-mv-gray-600 text-sm mb-2">
                  Versie {documentVersions[1].version} - Laatst bijgewerkt: {documentVersions[1].updatedAt}
                </p>
                <div className="space-y-4 text-mv-gray-700">
                  <section>
                    <h3 className="font-medium">1. Welke gegevens verzamelen wij?</h3>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Accountgegevens: naam, e-mailadres</li>
                      <li>Documenten die u uploadt</li>
                      <li>Gebruiksgegevens en logbestanden</li>
                    </ul>
                  </section>
                  <section>
                    <h3 className="font-medium">2. Waarvoor gebruiken wij uw gegevens?</h3>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Om de dienst te leveren</li>
                      <li>Om AI-samenvattingen te genereren</li>
                      <li>Om de beveiliging te waarborgen</li>
                    </ul>
                  </section>
                  <section>
                    <h3 className="font-medium">3. Uw rechten (AVG/GDPR)</h3>
                    <p>U heeft recht op:</p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Inzage in uw gegevens (Art. 15)</li>
                      <li>Rectificatie van onjuiste gegevens (Art. 16)</li>
                      <li>Verwijdering van uw gegevens (Art. 17)</li>
                      <li>Dataportabiliteit (Art. 20)</li>
                    </ul>
                  </section>
                  <section>
                    <h3 className="font-medium">4. Bewaartermijnen</h3>
                    <p>Wij bewaren uw gegevens zolang u een account heeft. Na verwijdering worden gegevens binnen 30 dagen gewist.</p>
                  </section>
                  <section>
                    <h3 className="font-medium">5. Contact</h3>
                    <p>Voor vragen over privacy: privacy@myvault.nl</p>
                  </section>
                </div>
              </div>
            )}
          </div>

          {/* Scroll Hint */}
          {((activeTab === 'terms' && !termsScrolled) || (activeTab === 'privacy' && !privacyScrolled)) && (
            <div className="px-6 py-2 bg-mv-gray-50 border-t border-mv-gray-200 text-center">
              <p className="text-xs text-mv-gray-500 flex items-center justify-center gap-1">
                <svg className="w-4 h-4 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
                Scroll naar beneden om verder te lezen
              </p>
            </div>
          )}

          {/* Checkboxes */}
          <div className="p-6 border-t border-mv-gray-200 space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                disabled={!termsScrolled}
                className="mt-1 h-4 w-4 text-mv-blue-600 border-mv-gray-300 rounded focus:ring-mv-blue-600 disabled:opacity-50"
              />
              <span className={`text-sm ${termsScrolled ? 'text-mv-gray-700' : 'text-mv-gray-400'}`}>
                Ik heb de <strong>Algemene Voorwaarden</strong> gelezen en ga hiermee akkoord
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                disabled={!privacyScrolled}
                className="mt-1 h-4 w-4 text-mv-blue-600 border-mv-gray-300 rounded focus:ring-mv-blue-600 disabled:opacity-50"
              />
              <span className={`text-sm ${privacyScrolled ? 'text-mv-gray-700' : 'text-mv-gray-400'}`}>
                Ik heb het <strong>Privacybeleid</strong> gelezen en ga hiermee akkoord
              </span>
            </label>
          </div>

          {/* Actions */}
          <div className="px-6 py-4 bg-mv-gray-50 border-t border-mv-gray-200 flex items-center justify-between">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-mv-gray-600 hover:text-mv-gray-900"
            >
              Annuleren
            </button>
            <button
              onClick={handleAccept}
              disabled={!canAccept || acceptMutation.isPending}
              className="px-6 py-2.5 bg-mv-blue-600 text-white font-medium rounded-lg hover:bg-mv-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {acceptMutation.isPending ? 'Verwerken...' : 'Accepteren en doorgaan'}
            </button>
          </div>
        </div>

        {/* Info Text */}
        <p className="text-center text-xs text-mv-gray-500 mt-4">
          Door te accepteren bevestigt u dat u 18 jaar of ouder bent
        </p>
      </div>
    </div>
  );
}
